/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {ModelUsageComponent} from '../../../containers/pages/ScenarioAnalysis/ViewVariables/VariablesTable';
import {VariablesTableRow} from '../../../containers/pages/ScenarioAnalysis/ViewModel/VariablesTable';
import {RootState} from '../../store';
import {SCENARIO_ANALYSIS_CONSTANTS} from '../../../containers/pages/ScenarioAnalysis/constants';

interface IParameterListState {
  inputData: VariablesTableRow[]
  outputData: VariablesTableRow[]
  outputsNotUpdated: boolean
}

const defaultParameterListState: IParameterListState = {
  inputData: [],
  outputData: [],
  outputsNotUpdated: false
}

interface IGlobalVariableListState {
  isModelUsageDialogOpen: boolean
  variableModelUsageData: ModelUsageComponent
}

interface IState {
  parameterList: IParameterListState[]
  tabsState: ITabsState
  globalVariableListState: IGlobalVariableListState
}

export interface ITabsState {
  selectedVerticalTabIndex: number
  selectedHorizontalTabIndex: number
}

// const initialParameter
const initialState: IState = {
  parameterList: [],
  tabsState: {
    selectedVerticalTabIndex: SCENARIO_ANALYSIS_CONSTANTS.viewModel.constants.states.initTabIdx,
    selectedHorizontalTabIndex: SCENARIO_ANALYSIS_CONSTANTS.viewModel.constants.states.initTabIdx,
  },
  globalVariableListState: {
    isModelUsageDialogOpen: false,
    variableModelUsageData: {
      variableName: "",
      displayName: "",
    },
  }
} as IState

const scenarioAnalysis = createSlice({
  name: 'scenarioAnalysis',
  initialState,
  reducers: {
    setTabsState: (state, action: PayloadAction<ITabsState>) => void (state.tabsState = action.payload),
    setInputData: (state, action: PayloadAction<VariablesTableRow[]>) => {
      const selectedVerticalTabIndex = state.tabsState.selectedVerticalTabIndex
      let currentState = state.parameterList?.[state.tabsState.selectedVerticalTabIndex]
      if (currentState === undefined) {
        currentState = {...defaultParameterListState}
      }
      currentState.inputData = action.payload
      state.parameterList[selectedVerticalTabIndex] = currentState
    },
    setOutputData: (state, action: PayloadAction<VariablesTableRow[]>) => {
      const selectedVerticalTabIndex = state.tabsState.selectedVerticalTabIndex
      let currentState = state.parameterList?.[state.tabsState.selectedVerticalTabIndex]
      if (currentState === undefined) {
        currentState = {...defaultParameterListState}
      }
      currentState.outputData = action.payload
      state.parameterList[selectedVerticalTabIndex] = currentState
    },
    setOutputsNotUpdated: (state, action: PayloadAction<boolean>) => void (state.parameterList[state.tabsState.selectedVerticalTabIndex].outputsNotUpdated = action.payload),
    setIsModelUsageDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.globalVariableListState.isModelUsageDialogOpen = action.payload
    },
    setVariableModelUsageData: (state, action: PayloadAction<ModelUsageComponent>) => void (state.globalVariableListState.variableModelUsageData = action.payload),
  },
})

const selectTabsState = (state: RootState) => state.scenarioAnalysis.tabsState
const selectInputData = (state: RootState) => {
  return state.scenarioAnalysis.parameterList?.[selectTabsState(state).selectedVerticalTabIndex]?.inputData ?? []
}
const selectOutputData = (state: RootState) => state.scenarioAnalysis.parameterList?.[selectTabsState(state).selectedVerticalTabIndex]?.outputData ?? []
const selectCurrentParameterListState = (state: RootState) => state.scenarioAnalysis.parameterList?.[selectTabsState(state).selectedVerticalTabIndex]
const selectGlobalVariableListState = (state: RootState) => state.scenarioAnalysis.globalVariableListState

const {actions, reducer} = scenarioAnalysis
export const {
  setTabsState,
  setInputData,
  setOutputData,
  setOutputsNotUpdated,
  setIsModelUsageDialogOpen,
  setVariableModelUsageData
} = actions
export {
  selectTabsState,
  selectInputData,
  selectOutputData,
  selectCurrentParameterListState,
  selectGlobalVariableListState
}
export default reducer
